import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, Users, ArrowRightLeft, Calendar, X, Filter } from 'lucide-react';
import type { AppDispatch, RootState } from '@/store';
import {
  fetchAssignments,
  createAssignment,
  deleteAssignment,
  clearError,
} from '@/store/slices/resourceTeamAssignmentsSlice';
import { fetchResources } from '@/store/slices/resourcesSlice';
import { fetchTeams } from '@/store/slices/teamsSlice';
import { DataTable } from '@/components/ui';
import {
  GlassCard,
  GlassButton,
  GlassSelect,
  GlassPageHeader,
  GlassModal,
  GlassConfirmModal,
  GlassLoadingSpinner,
  GlassAlert,
  SectionReveal,
  GlassBadge,
} from '@/design-system';
import AssignmentForm from './components/AssignmentForm';
import TransferModal from './components/TransferModal';
import type { ResourceTeamAssignment, Column } from '@/types';

const ResourceTeamAssignmentsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    entities: assignments,
    loading,
    error,
  } = useSelector((state: RootState) => state.resourceTeamAssignments);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [isEndModalOpen, setIsEndModalOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<ResourceTeamAssignment | null>(null);
  const [showFilters, setShowFilters] = useState(true);
  const [activeOnly, setActiveOnly] = useState(true);

  useEffect(() => {
    dispatch(fetchAssignments({ active: activeOnly ? true : undefined }));
    dispatch(fetchResources({ active: true }));
    dispatch(fetchTeams());
  }, [dispatch, activeOnly]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  const handleCreateAssignment = async (data: Partial<ResourceTeamAssignment>) => {
    const result = await dispatch(createAssignment(data));
    if (createAssignment.fulfilled.match(result)) {
      setIsCreateModalOpen(false);
    }
  };

  const handleEndAssignment = async () => {
    if (!selectedAssignment) return;
    
    const result = await dispatch(deleteAssignment(selectedAssignment.id));
    if (deleteAssignment.fulfilled.match(result)) {
      setIsEndModalOpen(false);
      setSelectedAssignment(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusBadge = (assignment: ResourceTeamAssignment) => {
    const isActive = !assignment.endDate;
    return (
      <GlassBadge variant={isActive ? 'success' : 'default'} size="sm">
        {isActive ? 'Active' : 'Ended'}
      </GlassBadge>
    );
  };

  const columns: Column<ResourceTeamAssignment>[] = [
    {
      key: 'resource',
      header: 'Resource',
      sortable: true,
      render: (value, row) => {
        const resource = row.resource;
        return resource ? (
          <div>
            <div className="font-medium text-foreground text-[13px]">
              {resource.firstName} {resource.lastName}
            </div>
            <div className="text-xs text-muted-foreground font-mono">
              {resource.resourceId}
            </div>
          </div>
        ) : (
          <span className="text-muted-foreground">Unknown</span>
        );
      },
    },
    {
      key: 'team',
      header: 'Team',
      sortable: true,
      render: (value, row) => {
        const team = row.team;
        return team ? (
          <div>
            <div className="font-medium text-foreground text-[13px]">
              {team.name}
            </div>
            {team.program && (
              <div className="text-xs text-muted-foreground">
                {team.program.name}
              </div>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground">Unknown</span>
        );
      },
    },
    {
      key: 'startDate',
      header: 'Start Date',
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-1 text-sm text-foreground">
          <Calendar className="w-3.5 h-3.5" />
          {formatDate(value as string)}
        </div>
      ),
    },
    {
      key: 'endDate',
      header: 'End Date',
      sortable: true,
      render: (value) =>
        value ? (
          <div className="flex items-center gap-1 text-sm text-foreground">
            <Calendar className="w-3.5 h-3.5" />
            {formatDate(value as string)}
          </div>
        ) : (
          <span className="text-xs text-muted-foreground italic">Current</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (value, row) => getStatusBadge(row),
    },
  ];

  if (loading && assignments.length === 0) {
    return (
      <div className="min-h-screen glass-page-bg flex items-center justify-center">
        <GlassLoadingSpinner size="lg" variant="gradient" label="Loading assignments..." />
      </div>
    );
  }

  const activeAssignments = assignments.filter(a => !a.endDate);
  const endedAssignments = assignments.filter(a => a.endDate);

  const statusOptions = [
    { value: 'active', label: 'Active Only' },
    { value: 'all', label: 'All (Active + Ended)' },
  ];

  return (
    <div className="min-h-screen glass-page-bg p-6 lg:p-8">
      <SectionReveal>
        <GlassPageHeader
          title="Resource Assignments"
          subtitle="Manage resource-to-team assignments and transfers"
          icon={<Users className="w-6 h-6" />}
          iconGradient="blue-violet"
          className="mb-6"
          actions={
            <div className="flex items-center gap-3">
              <GlassButton
                variant="secondary"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                leftIcon={<Filter className="w-4 h-4" />}
              >
                {showFilters ? 'Hide' : 'Show'} Filters
              </GlassButton>
              <GlassButton
                variant="primary"
                size="sm"
                onClick={() => setIsCreateModalOpen(true)}
                leftIcon={<Plus className="w-4 h-4" />}
              >
                Assign Resource
              </GlassButton>
            </div>
          }
        />
      </SectionReveal>

      {error && (
        <SectionReveal>
          <GlassAlert
            variant="error"
            message={error}
            onClose={() => dispatch(clearError())}
            className="mb-6"
          />
        </SectionReveal>
      )}

      {showFilters && (
        <SectionReveal delay={0.1}>
          <GlassCard className="mb-6">
            <div className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block text-xs font-medium uppercase tracking-wide text-[var(--muted)]">
                    Assignment Status
                  </label>
                  <GlassSelect
                    value={activeOnly ? 'active' : 'all'}
                    onChange={(value) => setActiveOnly(value === 'active')}
                    options={statusOptions}
                    placeholder="Filter by status"
                    fullWidth
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-xs font-medium uppercase tracking-wide text-[var(--muted)]">
                    Summary
                  </label>
                  <div className="flex items-center gap-4 text-sm h-10 px-3 rounded-xl bg-[var(--glass-subtle)]">
                    <div>
                      <span className="font-semibold text-emerald-600">{activeAssignments.length}</span>
                      <span className="text-[var(--muted)] ml-1">Active</span>
                    </div>
                    <div>
                      <span className="font-semibold text-[var(--text)]">{endedAssignments.length}</span>
                      <span className="text-[var(--muted)] ml-1">Ended</span>
                    </div>
                    <div>
                      <span className="font-semibold text-[var(--text)]">{assignments.length}</span>
                      <span className="text-[var(--muted)] ml-1">Total</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </GlassCard>
        </SectionReveal>
      )}

      <SectionReveal delay={0.2}>
        <GlassCard padding="none">
          <div className="max-h-[calc(100vh-350px)] overflow-auto">
            <DataTable
              data={assignments}
              columns={columns}
              searchable
              searchPlaceholder="Search assignments..."
              sortable
              pagination
              pageSize={25}
              emptyMessage="No assignments found"
              emptyIcon={<Users className="w-12 h-12 text-muted-foreground" />}
              actions={(row) => {
                const isActive = !row.endDate;
                return (
                  <div className="flex items-center gap-2">
                    {isActive && (
                      <button
                        onClick={() => {
                          setSelectedAssignment(row);
                          setIsTransferModalOpen(true);
                        }}
                        className="p-1 text-muted-foreground hover:text-primary-600 transition-colors"
                        title="Transfer to another team"
                        aria-label="Transfer to another team"
                      >
                        <ArrowRightLeft className="w-4 h-4" />
                      </button>
                    )}
                    {isActive && (
                      <button
                        onClick={() => {
                          setSelectedAssignment(row);
                          setIsEndModalOpen(true);
                        }}
                        className="p-1 text-muted-foreground hover:text-status-error transition-colors"
                        title="End assignment"
                        aria-label="End assignment"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              }}
            />
          </div>
        </GlassCard>
      </SectionReveal>

      {/* Create Assignment Modal */}
      <GlassModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Assign Resource to Team"
        subtitle="Create a new team assignment"
        icon={<Users className="w-5 h-5" />}
        iconGradient="blue-violet"
        size="lg"
      >
        <AssignmentForm
          onSubmit={handleCreateAssignment}
          onCancel={() => setIsCreateModalOpen(false)}
          isLoading={loading}
        />
      </GlassModal>

      {/* Transfer Modal */}
      <TransferModal
        isOpen={isTransferModalOpen}
        onClose={() => {
          setIsTransferModalOpen(false);
          setSelectedAssignment(null);
        }}
        assignment={selectedAssignment}
      />

      {/* End Assignment Confirmation Modal */}
      <GlassConfirmModal
        isOpen={isEndModalOpen}
        onClose={() => {
          setIsEndModalOpen(false);
          setSelectedAssignment(null);
        }}
        onConfirm={handleEndAssignment}
        title="End Assignment"
        message={
          selectedAssignment?.resource && selectedAssignment?.team
            ? `Are you sure you want to end the assignment of ${selectedAssignment.resource.firstName} ${selectedAssignment.resource.lastName} to ${selectedAssignment.team.name}? This will set the end date to today.`
            : ''
        }
        confirmLabel="End Assignment"
        variant="danger"
        isLoading={loading}
      />
    </div>
  );
};

export default ResourceTeamAssignmentsPage;
